import { getCurrentUser } from '../auth.js';
import { LiveRecorder } from '../live/LiveRecorder.js';
import { LiveSessionClient } from '../live/LiveSessionClient.js';
import { openAudioSetupWizard } from '../components/AudioSetupWizard.js';
import { getLiveCopy, getLanguage } from '../i18n.js';
import { encodeJsonPayload } from '../utils/base64.js';
import { eventTypeOrder, labelForEventSubtype, labelForEventType, normalizeEventType } from '../utils/eventTypeLabels.js';
import { canAccessAppFactory } from '../versionAccess.js';
import {
  listAppFactorySessions,
  createAppFactorySession,
  getAppFactorySession,
  analyzeAppFactoryProgress,
  updateAppFactoryRequirements,
  retryBillingStart,
} from '../api.js';

const html = String.raw;
const appConfig = window.__ANFORDERUNGSAI_CONFIG__ ?? {};

const formatCopy = (template, replacements = {}) => {
  if (!template) return '';
  return template.replace(/{{(.*?)}}/g, (_, key) => {
    const value = replacements[key.trim()];
    return value === undefined || value === null ? '' : value;
  });
};

const renderRequirementCard = (req, copy) => html`
  <div class="card live-panel stack tight">
    <strong>
      ${labelForEventType(req.eventType ?? req.type, copy?.requirementsList?.types ?? copy?.types ?? {})}
      ${labelForEventSubtype(req.eventSubtype, copy?.requirementsList?.types ?? copy?.types ?? {}) ? ` · ${labelForEventSubtype(req.eventSubtype, copy?.requirementsList?.types ?? copy?.types ?? {})}` : ''}
    </strong>
    <p>${req.text ?? ''}</p>
  </div>
`;

const renderRequirementGroups = (items, copy) => {
  const grouped = eventTypeOrder.reduce((acc, type) => {
    acc[type] = [];
    return acc;
  }, {});
  (Array.isArray(items) ? items : []).forEach((item) => {
    const type = normalizeEventType(item?.eventType ?? item?.type, 'sonstiges');
    if (!grouped[type]) grouped[type] = [];
    grouped[type].push(item);
  });
  return eventTypeOrder
    .filter((type) => (grouped[type] ?? []).length > 0)
    .map((type) => html`
      <section class="stack tight live-event-group">
        <div class="row" style="justify-content: space-between; align-items: center;">
          <strong>${labelForEventType(type, copy?.requirementsList?.types ?? copy?.types ?? {})}</strong>
          <span class="muted small">${grouped[type].length}</span>
        </div>
        ${grouped[type].map((item) => renderRequirementCard(item, copy)).join('')}
      </section>
    `)
    .join('');
};

const renderDeferredDecisionCard = (decision, copy) => {
  const start = Number(decision?.sourceRange?.startSec);
  const end = Number(decision?.sourceRange?.endSec);
  const hasRange = Number.isFinite(start) && Number.isFinite(end);
  const speaker = typeof decision?.sourceRange?.speaker === 'string' ? decision.sourceRange.speaker.trim() : '';
  return html`
    <div class="card live-panel stack tight">
      <strong>${decision?.status ?? copy?.deferredDecisions?.fallbackStatus ?? 'Vertagt'}</strong>
      <p>${decision?.text ?? ''}</p>
      ${
        hasRange
          ? `<p class="muted small">${copy?.deferredDecisions?.sourceLabel ?? 'Quelle'}: ${start.toFixed(1)}s - ${end.toFixed(1)}s${speaker ? ` (${speaker})` : ''}</p>`
          : ''
      }
      ${decision?.rationale ? `<p class="muted small">${decision.rationale}</p>` : ''}
    </div>
  `;
};

const createStatusUpdater = (element) => (message) => {
  if (!element) return;
  element.textContent = message;
};

const getTodayDateString = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

const renderLiveSessionViewInternal = (container) => {
  const copy = getLiveCopy();
  const appFactoryCopy = copy.appFactory ?? {};
  const currentUser = getCurrentUser();
  const hasAppFactoryAccess = canAccessAppFactory(currentUser);
  const LANGUAGE_OPTIONS = [
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  ];
  const AUDIO_MODES = copy.audioModes ?? [
    { code: 'mix', label: 'System + Mikrofon', description: 'Systemaudio und Mikrofon parallel aufnehmen' },
  ];
  const DEFAULT_AUDIO_MODE = AUDIO_MODES[0]?.code ?? 'mix';
  const DOMAIN_OPTIONS = Array.isArray(copy.domainOptions) && copy.domainOptions.length
    ? copy.domainOptions
    : [
        { value: 'enterprise-software', label: 'Enterprise-Software' },
        { value: 'bau', label: 'Bau' },
        { value: 'maschinenbau', label: 'Maschinenbau' },
        { value: 'produktentwicklung', label: 'Produktentwicklung' },
        { value: 'sonstiges', label: 'Sonstiges' },
      ];
  const DEFAULT_DOMAIN = copy.domainDefault ?? DOMAIN_OPTIONS[0]?.value ?? 'enterprise-software';
  const ENGINE_CATALOG = {
    mistral: {
      code: 'mistral',
      label: 'Mistral (Voxtral)',
      description: 'Default transcription via Mistral Voxtral.',
    },
    'aws-transcribe': {
      code: 'aws-transcribe',
      label: 'AWS Transcribe',
      description: 'AWS Live Transcribe (beta).',
    },
  };
  const configuredEngines = Array.isArray(appConfig.transcriptionEngines)
    ? appConfig.transcriptionEngines.map((value) => String(value).toLowerCase())
    : ['mistral', 'aws-transcribe'];
  let ENGINE_OPTIONS = configuredEngines
    .map((code) => ENGINE_CATALOG[code])
    .filter(Boolean);
  if (!ENGINE_OPTIONS.length) {
    ENGINE_OPTIONS = [ENGINE_CATALOG.mistral];
  }
  const DEFAULT_LANGUAGE = getLanguage();
  const configuredDefaultEngine = typeof appConfig.transcriptionEngineDefault === 'string'
    ? String(appConfig.transcriptionEngineDefault).toLowerCase()
    : ENGINE_OPTIONS[0]?.code;
  const fallbackEngine = (ENGINE_OPTIONS.find((option) => option.code === configuredDefaultEngine)
    ?? ENGINE_OPTIONS[0]
    ?? ENGINE_CATALOG.mistral).code;
  const preferredLiveEngine = 'aws-transcribe';
  const DEFAULT_ENGINE = ENGINE_OPTIONS.some((option) => option.code === preferredLiveEngine)
    ? preferredLiveEngine
    : fallbackEngine;
  const languageButtonsMarkup = LANGUAGE_OPTIONS.map(
    (option) => html`<button
      type="button"
      class="language-pill"
      data-language="${option.code}"
      title="${option.label}"
    >
      <span class="language-pill__flag" aria-hidden="true">${option.flag}</span>
      <span>${option.label}</span>
    </button>`,
  ).join('');
  const DEFAULT_ENGINE_LABEL = ENGINE_CATALOG[DEFAULT_ENGINE]?.label
    ?? (DEFAULT_ENGINE === 'aws-transcribe' ? 'AWS Transcribe' : 'Mistral (Voxtral)');
  const DEFAULT_ENGINE_DESCRIPTION = ENGINE_OPTIONS.find((option) => option.code === DEFAULT_ENGINE)?.description ?? '';
  const engineButtonsMarkup = ENGINE_OPTIONS.map(
    (option) => html`<button
      type="button"
      class="language-pill"
      data-engine="${option.code}"
      title="${option.description}"
    >${option.label}</button>`,
  ).join('');
  const domainButtonsMarkup = DOMAIN_OPTIONS.map(
    (option) => html`<button
      type="button"
      class="language-pill"
      data-domain="${option.value}"
      title="${option.label}"
    >${option.label}</button>`,
  ).join('');

  container.innerHTML = html`
    <div class="stack live-session">
      <div class="view-header row" style="justify-content: space-between; align-items: flex-start; gap: 1rem;">
        <div class="stack tight">
          <h1>${copy.title}</h1>
        </div>
        <button type="button" id="start-live">${copy.buttons.start}</button>
      </div>
      <section class="card stack">
        <button type="button" id="toggle-settings" class="ghost-button align-start">${copy.buttons.settingsOpen}</button>
        <div id="live-settings" class="stack tight live-settings-panel hidden">
          <div class="row" id="language-selector">
            ${languageButtonsMarkup}
          </div>
          <div class="stack tight">
            <div class="row" id="engine-selector">
              ${engineButtonsMarkup}
            </div>
            <p class="muted small" id="engine-description">${DEFAULT_ENGINE_DESCRIPTION}</p>
          </div>
          <div class="row" id="audio-mode-selector">
            ${AUDIO_MODES.map(
              (mode) => html`<button
                type="button"
                class="language-pill"
                data-audio-mode="${mode.code}"
                title="${mode.description}"
              >${mode.label}</button>`,
            ).join('')}
          </div>
          <div class="stack tight">
            <span class="muted small">${copy.domainLabel ?? 'Themenbereich'}</span>
            <div class="row" id="domain-selector">
              ${domainButtonsMarkup}
            </div>
          </div>
        </div>
        <section class="stack tight">
          <div class="row live-input-row">
            <label class="live-input">
              ${copy.fields.customerLabel}
              <input type="text" id="live-customer" placeholder="${copy.fields.customerPlaceholder}" />
            </label>
            <label class="live-input">
              ${copy.fields.projectLabel}
              <input type="text" id="live-project" placeholder="${copy.fields.projectPlaceholder}" />
            </label>
            <label class="live-input">
              ${copy.fields.meetingLabel ?? 'Meeting'}
              <input type="date" id="live-meeting" />
            </label>
            <div class="stack tight live-input-status">
              <div class="row" style="justify-content: space-between; align-items:center;">
                <span class="muted small">${copy.info.engineLabel}: <span id="live-engine-status">${DEFAULT_ENGINE_LABEL}</span></span>
                <span class="muted small">${copy.info.languageLabel}: <span id="live-language-status">${copy.info.languageUnknown}</span></span>
              </div>
              <div id="live-status" class="muted">${copy.info.statusReady}</div>
              <p class="muted small hidden" id="live-extraction-warning"></p>
            </div>
          </div>
        </section>
        <section class="stack tight">
          <span class="muted small" id="audio-active-source">–</span>
          <div class="audio-meter-row hidden" data-meter="system">
            <span class="muted small audio-meter-label">${copy.audio.systemLabel}</span>
            <div class="audio-meter-track">
              <div class="audio-meter-fill" style="background:#60a5fa;"></div>
            </div>
          </div>
          <div class="audio-meter-row" data-meter="mic">
            <span class="muted small audio-meter-label">${copy.audio.micLabel}</span>
            <div class="audio-meter-track">
              <div class="audio-meter-fill" style="background:#f97316;"></div>
            </div>
          </div>
        </section>
        <section class="stack tight">
          <div class="row" style="justify-content: space-between; align-items: center;">
            <h3>${copy.requirements.title}</h3>
            <button type="button" id="share-requirements" class="ghost-button">${copy.buttons.share}</button>
          </div>
          <div id="live-requirements" class="stack tight live-panel">
            <p class="muted">${copy.requirements.empty}</p>
          </div>
          <div class="stack tight">
            <input type="text" id="share-link" class="share-link hidden" readonly />
            <p class="muted small" id="share-feedback"></p>
          </div>
        </section>
        <section class="stack tight">
          <div class="row" style="justify-content: space-between; align-items: center;">
            <h3>${copy.deferredDecisions?.title ?? 'Vertagte Entscheidungen'}</h3>
          </div>
          <div id="live-deferred-decisions" class="stack tight live-panel">
            <p class="muted">${copy.deferredDecisions?.empty ?? 'Noch keine vertagten Entscheidungen vorhanden.'}</p>
          </div>
        </section>
        ${
          hasAppFactoryAccess
            ? html`
                <section class="card stack app-factory-panel" data-app-factory-panel>
                  <div class="row" style="justify-content: space-between; align-items: center; gap: 12px;">
                    <div>
                      <h3>${copy.appFactory.title}</h3>
                      <p class="muted small">${copy.appFactory.description}</p>
                    </div>
                    <button type="button" id="app-factory-open" class="ghost-button small">${copy.appFactory.buttons.open}</button>
                  </div>
                  <div class="stack tight" id="app-factory-session-controls">
                    <label class="stack tight">
                      <span class="muted small">${copy.appFactory.sessionSelectLabel}</span>
                      <select id="app-factory-session-select"></select>
                    </label>
                    <div class="row" style="flex-wrap: wrap; gap: 8px;">
                      <button type="button" id="app-factory-refresh" class="ghost-button small">
                        ${copy.appFactory.buttons.refresh}
                      </button>
                      <button type="button" id="app-factory-new-session" class="ghost-button small">
                        ${copy.appFactory.buttons.newSession}
                      </button>
                      <button type="button" id="app-factory-analyze" class="ghost-button small">
                        ${copy.appFactory.buttons.analyze}
                      </button>
                    </div>
                    <label class="row" style="align-items: center; gap: 8px;">
                      <input type="checkbox" id="app-factory-auto-sync" />
                      <span class="muted small">${copy.appFactory.autoSyncLabel}</span>
                    </label>
                    <p class="muted small" id="app-factory-status">${copy.appFactory.status.selectSession}</p>
                  </div>
                  <div id="app-factory-progress" class="app-factory-progress muted small">
                    ${copy.appFactory.guidelineEmpty}
                  </div>
                  <div class="stack tight" id="app-factory-requirements-panel">
                    <div class="row" style="justify-content: space-between; align-items: center;">
                      <h4>${copy.appFactory.requirementsTitle}</h4>
                      <button type="button" id="app-factory-sync" class="ghost-button small">
                        ${copy.appFactory.buttons.sync}
                      </button>
                    </div>
                    <p class="muted small" id="app-factory-requirements-hint">${copy.appFactory.requirementsHint}</p>
                  </div>
                </section>
              `
            : ''
        }
        <section class="stack tight">
          <button type="button" id="toggle-transcript" class="ghost-button align-start">${copy.buttons.transcriptShow}</button>
          <div id="live-transcript-container" class="hidden">
            <div id="live-transcript" class="live-panel muted transcript-panel">
              ${copy.transcript.empty}
            </div>
          </div>
        </section>
        <p class="muted small footnote align-center">
          ${copy.info.footer}
        </p>
      </section>
    </div>
  `;

  const state = {
    active: false,
    recorder: null,
    client: null,
    requirements: [],
    deferredDecisions: [],
    language: DEFAULT_LANGUAGE,
    selectedLanguage: DEFAULT_LANGUAGE,
    languageManual: true,
    awaitingFirstChunk: false,
    audioMode: DEFAULT_AUDIO_MODE,
    engine: DEFAULT_ENGINE,
    domain: DEFAULT_DOMAIN,
    extractionWarnings: [],
    settingsOpen: false,
    transcriptExpanded: false,
    wakeLock: null,
    wizardOpen: false,
    appFactory: {
      enabled: hasAppFactoryAccess,
      sessions: [],
      selectedSessionId: null,
      session: null,
      loadingSessions: false,
      loadingSession: false,
      syncing: false,
      analyzing: false,
      creating: false,
      autoSync: false,
      requirementsDirty: false,
      statusMessage: '',
    },
  };

  const startButton = container.querySelector('#start-live');
  const statusEl = container.querySelector('#live-status');
  const transcriptEl = container.querySelector('#live-transcript');
  const requirementsEl = container.querySelector('#live-requirements');
  const deferredDecisionsEl = container.querySelector('#live-deferred-decisions');
  const shareButton = container.querySelector('#share-requirements');
  const shareLinkInput = container.querySelector('#share-link');
  const shareFeedback = container.querySelector('#share-feedback');
  const customerInput = container.querySelector('#live-customer');
  const projectInput = container.querySelector('#live-project');
  const meetingInput = container.querySelector('#live-meeting');
  const languageStatusEl = container.querySelector('#live-language-status');
  const engineStatusEl = container.querySelector('#live-engine-status');
  const engineDescriptionEl = container.querySelector('#engine-description');
  const extractionWarningEl = container.querySelector('#live-extraction-warning');
  const languageSelector = container.querySelector('#language-selector');
  const languageButtons = Array.from(languageSelector?.querySelectorAll('.language-pill') ?? []);
  const engineSelector = container.querySelector('#engine-selector');
  const engineButtons = Array.from(engineSelector?.querySelectorAll('[data-engine]') ?? []);
  const audioModeButtons = Array.from(container.querySelectorAll('[data-audio-mode]'));
  const domainSelector = container.querySelector('#domain-selector');
  const domainButtons = Array.from(domainSelector?.querySelectorAll('[data-domain]') ?? []);
  const audioSourceStatus = container.querySelector('#audio-active-source');
  const systemMeterRow = container.querySelector('[data-meter="system"]');
  const micMeterRow = container.querySelector('[data-meter="mic"]');
  const systemMeterFill = systemMeterRow?.querySelector('.audio-meter-fill');
  const micMeterFill = micMeterRow?.querySelector('.audio-meter-fill');
  const toggleSettingsButton = container.querySelector('#toggle-settings');
  const settingsPanel = container.querySelector('#live-settings');
  const toggleTranscriptButton = container.querySelector('#toggle-transcript');
  const transcriptContainer = container.querySelector('#live-transcript-container');
  const appFactoryPanel = container.querySelector('[data-app-factory-panel]');
  const appFactorySelect = container.querySelector('#app-factory-session-select');
  const appFactoryRefreshButton = container.querySelector('#app-factory-refresh');
  const appFactoryNewButton = container.querySelector('#app-factory-new-session');
  const appFactoryAnalyzeButton = container.querySelector('#app-factory-analyze');
  const appFactorySyncButton = container.querySelector('#app-factory-sync');
  const appFactoryStatusEl = container.querySelector('#app-factory-status');
  const appFactoryProgressEl = container.querySelector('#app-factory-progress');
  const appFactoryRequirementsHint = container.querySelector('#app-factory-requirements-hint');
  const appFactoryAutoSyncCheckbox = container.querySelector('#app-factory-auto-sync');
  const appFactoryOpenButton = container.querySelector('#app-factory-open');

  if (meetingInput && !meetingInput.value) {
    meetingInput.value = getTodayDateString();
  }

  const appFactoryRefs = {
    panel: appFactoryPanel,
    select: appFactorySelect,
    refreshButton: appFactoryRefreshButton,
    newButton: appFactoryNewButton,
    analyzeButton: appFactoryAnalyzeButton,
    syncButton: appFactorySyncButton,
    status: appFactoryStatusEl,
    progress: appFactoryProgressEl,
    requirementsHint: appFactoryRequirementsHint,
    autoSync: appFactoryAutoSyncCheckbox,
    openButton: appFactoryOpenButton,
  };

  const applyMeterFill = (fillEl, level, isActive, activeColor, inactiveColor) => {
    if (!fillEl) return;
    const normalized = Math.max(0, Math.min(1, level * 6));
    fillEl.style.width = `${(normalized * 100).toFixed(0)}%`;
    fillEl.style.background = isActive ? activeColor : inactiveColor;
  };

  const updateAppFactoryStatus = (message) => {
    if (!appFactoryRefs.status) return;
    if (!message) {
      appFactoryRefs.status.textContent = '';
      return;
    }
    appFactoryRefs.status.textContent = message;
  };

  const renderAppFactorySessionOptions = () => {
    if (!appFactoryRefs.select) return;
    const placeholder = appFactoryCopy.sessionSelectPlaceholder ?? 'Session auswählen…';
    const sessions = Array.isArray(state.appFactory.sessions) ? state.appFactory.sessions : [];
    const options = [`<option value="">${placeholder}</option>`];
    sessions.forEach((session) => {
      const baseLabel = session?.displayName
        ?? `${appFactoryCopy.sessionLabel ?? 'Session'} ${String(session.sessionId).slice(0, 8)}`;
      const metaParts = [];
      if (session?.customerId) {
        metaParts.push(`${appFactoryCopy.customerLabelShort ?? 'Kunde'}: ${session.customerId}`);
      }
      if (session?.projectId) {
        metaParts.push(`${appFactoryCopy.projectLabelShort ?? 'Projekt'}: ${session.projectId}`);
      }
      const statusPart = session?.status ? session.status : '';
      const label = [baseLabel, metaParts.join(' · '), statusPart].filter((part) => part && part.trim()).join(' · ');
      options.push(`<option value="${session.sessionId}">${label}</option>`);
    });
    appFactoryRefs.select.innerHTML = options.join('');
    if (state.appFactory.selectedSessionId) {
      appFactoryRefs.select.value = state.appFactory.selectedSessionId;
    }
  };

  const renderAppFactoryProgress = (progress) => {
    if (!appFactoryRefs.progress) return;
    if (!progress || !Array.isArray(progress.categories) || !progress.categories.length) {
      appFactoryRefs.progress.innerHTML = `<p class="muted small">${appFactoryCopy.guidelineEmpty ?? ''}</p>`;
      return;
    }
    const percent = Math.round(progress.percentComplete ?? 0);
    const completed = progress.completedCategories ?? 0;
    const total = progress.totalCategories ?? progress.categories.length;
    const categoriesMarkup = progress.categories
      .map(
        (category) => html`
          <div class="category-item ${category.completed ? 'completed' : 'pending'}">
            <div class="category-header">
              <span class="category-icon">${category.completed ? '✅' : '⏳'}</span>
              <h4>${category.name ?? ''}</h4>
              ${
                category.confidence
                  ? html`<span class="confidence">${Math.round(category.confidence * 100)}%</span>`
                  : ''
              }
            </div>
            <div class="questions-list">
              ${(category.questions ?? [])
                .map(
                  (question) => html`
                    <div class="question-item ${question.answered ? 'answered' : ''}">
                      <span class="question-icon">${question.answered ? '✓' : '○'}</span>
                      <span class="question-text">${question.text ?? ''}</span>
                      ${question.extractedInfo ? html`<p class="muted tiny">${question.extractedInfo}</p>` : ''}
                    </div>
                  `,
                )
                .join('')}
            </div>
          </div>
        `,
      )
      .join('');
    appFactoryRefs.progress.innerHTML = html`
      <div class="stack tight">
        <p class="muted small">
          ${completed} / ${total} (${percent}%)
        </p>
        <div class="categories-list">${categoriesMarkup}</div>
      </div>
    `;
  };

  const updateAppFactoryRequirementHint = () => {
    if (!appFactoryRefs.requirementsHint) return;
    if (!state.appFactory.selectedSessionId) {
      appFactoryRefs.requirementsHint.textContent = appFactoryCopy.status?.selectSession ?? '';
      return;
    }
    const count = state.requirements.length;
    if (!count) {
      appFactoryRefs.requirementsHint.textContent = appFactoryCopy.status?.syncEmpty ?? '';
      return;
    }
    const template = appFactoryCopy.requirementsSummary ?? '{{count}} Anforderungen bereit.';
    appFactoryRefs.requirementsHint.textContent = formatCopy(template, { count });
  };

  const maybeAutoSyncAppFactory = () => {
    if (!state.appFactory.enabled) return;
    if (!state.appFactory.autoSync) return;
    if (!state.appFactory.requirementsDirty) return;
    if (state.appFactory.syncing) return;
    syncAppFactoryRequirements({ silent: true });
  };

  const syncAppFactoryRequirements = async ({ silent = false } = {}) => {
    if (!state.appFactory.enabled || !appFactoryRefs.syncButton) return;
    if (!state.appFactory.selectedSessionId) {
      updateAppFactoryStatus(appFactoryCopy.status?.selectSession ?? '');
      return;
    }
    const requirementCount = state.requirements.length;
    if (!requirementCount) {
      updateAppFactoryStatus(appFactoryCopy.status?.syncEmpty ?? '');
      // Continue anyway so the backend decides what to do (e.g. keep previous set)
    }
    state.appFactory.syncing = true;
    if (appFactoryRefs.syncButton) {
      appFactoryRefs.syncButton.disabled = true;
    }
    updateAppFactoryStatus(appFactoryCopy.status?.syncing ?? '');
    try {
      await updateAppFactoryRequirements(state.appFactory.selectedSessionId, {
        items: state.requirements,
      });
      state.appFactory.requirementsDirty = false;
      if (!silent) {
        updateAppFactoryStatus(appFactoryCopy.status?.syncSuccess ?? '');
      }
    } catch (error) {
      console.error('Failed to sync app factory requirements', error);
      updateAppFactoryStatus(appFactoryCopy.errors?.sync ?? '');
    } finally {
      if (appFactoryRefs.syncButton) {
        appFactoryRefs.syncButton.disabled = false;
      }
      state.appFactory.syncing = false;
    }
  };

  const fetchAppFactorySession = async (sessionId) => {
    if (!state.appFactory.enabled || !sessionId) {
      state.appFactory.session = null;
      renderAppFactoryProgress(null);
      updateAppFactoryRequirementHint();
      return;
    }
    state.appFactory.loadingSession = true;
    updateAppFactoryStatus(appFactoryCopy.status?.loadingSession ?? '');
    try {
      const response = await getAppFactorySession(sessionId);
      state.appFactory.session = response?.session ?? null;
      renderAppFactoryProgress(state.appFactory.session?.progress ?? null);
      updateAppFactoryRequirementHint();
      const statusTemplate = appFactoryCopy.status?.updatedAt;
      if (statusTemplate) {
        updateAppFactoryStatus(
          formatCopy(statusTemplate, { time: new Date().toLocaleTimeString() }),
        );
      } else {
        updateAppFactoryStatus('');
      }
    } catch (error) {
      console.error('Failed to load app factory session', error);
      updateAppFactoryStatus(appFactoryCopy.errors?.session ?? '');
    } finally {
      state.appFactory.loadingSession = false;
    }
  };

  const selectDefaultAppFactorySession = () => {
    if (!state.appFactory.sessions.length) {
      state.appFactory.selectedSessionId = null;
      return;
    }
    if (
      state.appFactory.selectedSessionId &&
      state.appFactory.sessions.some((session) => session.sessionId === state.appFactory.selectedSessionId)
    ) {
      return;
    }
    state.appFactory.selectedSessionId = state.appFactory.sessions[0].sessionId;
  };

  const loadAppFactorySessions = async (options = {}) => {
    if (!state.appFactory.enabled) return;
    state.appFactory.loadingSessions = true;
    updateAppFactoryStatus(appFactoryCopy.status?.loadingSessions ?? '');
    try {
      const response = await listAppFactorySessions();
      state.appFactory.sessions = Array.isArray(response?.sessions) ? response.sessions : [];
      if (options.selectSessionId) {
        state.appFactory.selectedSessionId = options.selectSessionId;
      } else {
        selectDefaultAppFactorySession();
      }
      renderAppFactorySessionOptions();
      if (state.appFactory.selectedSessionId) {
        await fetchAppFactorySession(state.appFactory.selectedSessionId);
      } else if (!state.appFactory.sessions.length) {
        updateAppFactoryStatus(appFactoryCopy.sessionEmpty ?? '');
        renderAppFactoryProgress(null);
        updateAppFactoryRequirementHint();
      }
    } catch (error) {
      console.error('Failed to load app factory sessions', error);
      updateAppFactoryStatus(appFactoryCopy.errors?.load ?? '');
    } finally {
      state.appFactory.loadingSessions = false;
    }
  };

  const handleCreateAppFactorySession = async () => {
    if (!state.appFactory.enabled) return;
    state.appFactory.creating = true;
    if (appFactoryRefs.newButton) {
      appFactoryRefs.newButton.disabled = true;
    }
    const creatingMessage = appFactoryCopy.status?.creating ?? appFactoryCopy.creating ?? '';
    updateAppFactoryStatus(creatingMessage);
    try {
      const customerId = window.prompt(appFactoryCopy.prompts?.customer ?? '') || undefined;
      const projectId = window.prompt(appFactoryCopy.prompts?.project ?? '') || undefined;
      const response = await createAppFactorySession({
        guidelineId: 'default-v1',
        customerId,
        projectId,
      });
      const newSessionId = response?.sessionId;
      await loadAppFactorySessions({ selectSessionId: newSessionId });
    } catch (error) {
      console.error('Failed to create app factory session', error);
      updateAppFactoryStatus(appFactoryCopy.errors?.create ?? '');
    } finally {
      if (appFactoryRefs.newButton) {
        appFactoryRefs.newButton.disabled = false;
      }
      state.appFactory.creating = false;
    }
  };

  const handleAnalyzeAppFactorySession = async () => {
    if (!state.appFactory.enabled) return;
    if (!state.appFactory.selectedSessionId) {
      updateAppFactoryStatus(appFactoryCopy.status?.selectSession ?? '');
      return;
    }
    state.appFactory.analyzing = true;
    if (appFactoryRefs.analyzeButton) {
      appFactoryRefs.analyzeButton.disabled = true;
    }
    updateAppFactoryStatus(appFactoryCopy.status?.analyzing ?? '');
    try {
      await analyzeAppFactoryProgress(state.appFactory.selectedSessionId);
      await fetchAppFactorySession(state.appFactory.selectedSessionId);
    } catch (error) {
      console.error('Failed to analyze app factory session', error);
      updateAppFactoryStatus(appFactoryCopy.errors?.analyze ?? '');
    } finally {
      state.appFactory.analyzing = false;
      if (appFactoryRefs.analyzeButton) {
        appFactoryRefs.analyzeButton.disabled = false;
      }
    }
  };

  const updateAudioMeters = ({ system = 0, mic = 0, preferred = 'mic', showSystem = false } = {}) => {
    if (systemMeterRow) {
      systemMeterRow.classList.toggle('hidden', !showSystem);
      applyMeterFill(systemMeterFill, showSystem ? system : 0, showSystem && preferred === 'system', '#60a5fa', 'rgba(96,165,250,0.35)');
    }
    if (micMeterRow) {
      applyMeterFill(micMeterFill, mic, preferred === 'mic', '#f97316', 'rgba(249,115,22,0.35)');
    }
    if (audioSourceStatus) {
      audioSourceStatus.textContent = showSystem && preferred === 'system'
        ? copy.audio.sourceSystem
        : copy.audio.sourceMic;
    }
  };

  const requestWakeLock = async () => {
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) {
      return;
    }
    try {
      if (state.wakeLock) {
        await state.wakeLock.release();
      }
      state.wakeLock = await navigator.wakeLock.request('screen');
      state.wakeLock.addEventListener('release', () => {
        state.wakeLock = null;
      });
    } catch (error) {
      console.warn('Wake lock request failed', error);
    }
  };

  const releaseWakeLock = async () => {
    if (!state.wakeLock) return;
    try {
      await state.wakeLock.release();
    } catch (error) {
      console.warn('Wake lock release failed', error);
    } finally {
      state.wakeLock = null;
    }
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && state.active) {
      requestWakeLock();
    }
  });

  updateAudioMeters({ system: 0, mic: 0, preferred: 'mic', showSystem: false });

  const updateStatus = createStatusUpdater(statusEl);
  const resetButtonStyle = () => {
    if (!startButton) return;
    startButton.style.background = '';
    startButton.style.color = '';
  };
  const setButtonPending = () => {
    if (!startButton) return;
    startButton.style.background = '#fb923c';
    startButton.style.color = '#0f172a';
  };
  const setButtonSuccess = () => {
    if (!startButton) return;
    startButton.style.background = '#22c55e';
    startButton.style.color = '#0f172a';
  };
  const findLanguageMeta = (code) => {
    if (!code) return null;
    const normalized = String(code).toLowerCase();
    return LANGUAGE_OPTIONS.find((option) => option.code === normalized) ?? null;
  };
  const formatLanguageLabel = (code) => {
    const meta = findLanguageMeta(code);
    if (!meta) {
      return code ? String(code).toUpperCase() : null;
    }
    return `${meta.flag} ${meta.label}`;
  };
  const findEngineMeta = (code) => ENGINE_OPTIONS.find((option) => option.code === code) ?? null;
  const formatEngineLabel = (code) => {
    const meta = findEngineMeta(code);
    if (!meta) {
      return code === 'aws-transcribe' ? 'AWS Transcribe' : 'Mistral (Voxtral)';
    }
    return meta.label;
  };
  const updateEngineStatus = () => {
    if (!engineStatusEl) return;
    engineStatusEl.textContent = formatEngineLabel(state.engine);
    if (engineDescriptionEl) {
      engineDescriptionEl.textContent = findEngineMeta(state.engine)?.description ?? '';
    }
  };
  const updateExtractionWarning = (warnings = []) => {
    state.extractionWarnings = Array.isArray(warnings) ? warnings : [];
    if (!extractionWarningEl) return;
    const hasWarning = state.extractionWarnings.length > 0;
    extractionWarningEl.classList.toggle('hidden', !hasWarning);
    extractionWarningEl.textContent = hasWarning ? copy.info.extractionWarning : '';
  };
  const refreshEngineButtons = () => {
    engineButtons.forEach((button) => {
      const code = button.dataset.engine;
      button.classList.toggle('active', code === state.engine);
    });
    updateEngineStatus();
  };
  const updateLanguageStatus = (value, { manual = state.languageManual } = {}) => {
    if (!languageStatusEl) return;
    if (!value) {
      languageStatusEl.textContent = copy.info.languageUnknown;
      return;
    }
    const label = formatLanguageLabel(value) ?? value;
    const suffix = manual ? copy.info.languageManualSuffix ?? '' : '';
    languageStatusEl.textContent = `${label}${suffix}`;
  };
  const refreshLanguageButtons = () => {
    languageButtons.forEach((button) => {
      const code = button.dataset.language;
      button.classList.toggle('active', code === state.selectedLanguage);
    });
  };
  refreshLanguageButtons();
  updateLanguageStatus(state.selectedLanguage, { manual: true });
  const refreshAudioModeButtons = () => {
    audioModeButtons.forEach((button) => {
      const mode = button.dataset.audioMode;
      button.classList.toggle('active', mode === state.audioMode);
    });
  };
  const refreshDomainButtons = () => {
    domainButtons.forEach((button) => {
      const value = button.dataset.domain;
      button.classList.toggle('active', value === state.domain);
    });
  };
  const updateSettingsVisibility = () => {
    if (!settingsPanel || !toggleSettingsButton) return;
    settingsPanel.classList.toggle('hidden', !state.settingsOpen);
    toggleSettingsButton.textContent = state.settingsOpen ? copy.buttons.settingsClose : copy.buttons.settingsOpen;
  };
  const updateTranscriptVisibility = () => {
    if (!transcriptContainer || !toggleTranscriptButton) return;
    transcriptContainer.classList.toggle('hidden', !state.transcriptExpanded);
    toggleTranscriptButton.textContent = state.transcriptExpanded
      ? copy.buttons.transcriptHide
      : copy.buttons.transcriptShow;
  };
  const applyEngineSelection = (code) => {
    const meta = findEngineMeta(code) ?? ENGINE_OPTIONS[0];
    state.engine = meta?.code ?? 'mistral';
    refreshEngineButtons();
  };
  const applyLanguageSelection = (code) => {
    const next = findLanguageMeta(code) ? code : DEFAULT_LANGUAGE;
    state.selectedLanguage = next;
    state.language = next;
    state.languageManual = true;
    refreshLanguageButtons();
    updateLanguageStatus(next, { manual: true });
  };

  const applyAudioMode = (mode) => {
    if (!AUDIO_MODES.some((entry) => entry.code === mode)) {
      state.audioMode = DEFAULT_AUDIO_MODE;
    } else {
      state.audioMode = mode;
    }
    refreshAudioModeButtons();
  };
  const applyDomainSelection = (value) => {
    const isKnown = DOMAIN_OPTIONS.some((option) => option.value === value);
    state.domain = isKnown ? value : DEFAULT_DOMAIN;
    refreshDomainButtons();
  };

  languageButtons.forEach((button) => {
    button.addEventListener('click', () => {
      if (state.active) {
        updateStatus(copy.statuses.settingsLockedLanguage);
        return;
      }
      applyLanguageSelection(button.dataset.language);
      refreshLanguageButtons();
    });
  });

  applyLanguageSelection(DEFAULT_LANGUAGE);
  engineButtons.forEach((button) => {
    button.addEventListener('click', () => {
      if (state.active) {
        updateStatus(copy.statuses.settingsLockedEngine);
        return;
      }
      applyEngineSelection(button.dataset.engine);
    });
  });
  applyEngineSelection(DEFAULT_ENGINE);
  audioModeButtons.forEach((button) => {
    button.addEventListener('click', () => {
      if (state.active) {
        updateStatus(copy.statuses.settingsLockedAudio);
        return;
      }
      applyAudioMode(button.dataset.audioMode);
    });
  });
  domainButtons.forEach((button) => {
    button.addEventListener('click', () => {
      if (state.active) {
        updateStatus(copy.statuses.settingsLockedDomain ?? copy.statuses.settingsLockedAudio);
        return;
      }
      applyDomainSelection(button.dataset.domain);
    });
  });
  applyAudioMode(state.audioMode);
  applyDomainSelection(DEFAULT_DOMAIN);
  refreshAudioModeButtons();
  refreshDomainButtons();
  updateSettingsVisibility();
  updateTranscriptVisibility();
  const initializeAppFactoryPanel = () => {
    if (!state.appFactory.enabled || !appFactoryRefs.panel) {
      return;
    }
    renderAppFactorySessionOptions();
    updateAppFactoryRequirementHint();
    loadAppFactorySessions();
    appFactoryRefs.select?.addEventListener('change', (event) => {
      const value = event.target.value || null;
      state.appFactory.selectedSessionId = value;
      state.appFactory.session = null;
      renderAppFactoryProgress(null);
      updateAppFactoryRequirementHint();
      if (value) {
        fetchAppFactorySession(value);
      }
    });
    appFactoryRefs.refreshButton?.addEventListener('click', () => loadAppFactorySessions());
    appFactoryRefs.newButton?.addEventListener('click', handleCreateAppFactorySession);
    appFactoryRefs.analyzeButton?.addEventListener('click', handleAnalyzeAppFactorySession);
    appFactoryRefs.syncButton?.addEventListener('click', () => syncAppFactoryRequirements());
    appFactoryRefs.autoSync?.addEventListener('change', (event) => {
      state.appFactory.autoSync = Boolean(event.target.checked);
      if (state.appFactory.autoSync) {
        maybeAutoSyncAppFactory();
      }
    });
    appFactoryRefs.openButton?.addEventListener('click', () => {
      if (state.appFactory.selectedSessionId) {
        window.location.hash = `#/app-factory/session/${state.appFactory.selectedSessionId}`;
      } else {
        window.location.hash = '#/app-factory';
      }
    });
  };

  try {
    initializeAppFactoryPanel();
  } catch (error) {
    console.error('App Factory panel failed to initialize', error);
    if (appFactoryRefs.status) {
      const fallback =
        appFactoryCopy.errors?.init ??
        'App Factory konnte nicht initialisiert werden.';
      appFactoryRefs.status.textContent = fallback;
    }
  }

  toggleSettingsButton?.addEventListener('click', () => {
    state.settingsOpen = !state.settingsOpen;
    updateSettingsVisibility();
  });

  toggleTranscriptButton?.addEventListener('click', () => {
    state.transcriptExpanded = !state.transcriptExpanded;
    updateTranscriptVisibility();
    if (state.transcriptExpanded) {
      transcriptEl?.scrollTo({ top: transcriptEl.scrollHeight, behavior: 'smooth' });
    }
  });

  const resetView = () => {
    transcriptEl.innerHTML = copy.transcript.empty;
    requirementsEl.innerHTML = `<p class="muted">${copy.requirements.empty}</p>`;
    if (deferredDecisionsEl) {
      deferredDecisionsEl.innerHTML = `<p class="muted">${copy.deferredDecisions?.empty ?? 'Noch keine vertagten Entscheidungen vorhanden.'}</p>`;
    }
    shareFeedback.textContent = '';
    if (shareLinkInput) {
      shareLinkInput.classList.add('hidden');
      shareLinkInput.value = '';
    }
    state.requirements = [];
    state.deferredDecisions = [];
    if (state.appFactory.enabled) {
      state.appFactory.requirementsDirty = false;
      updateAppFactoryRequirementHint();
    }
    state.language = state.selectedLanguage;
    state.languageManual = true;
    updateLanguageStatus(state.selectedLanguage, { manual: true });
    updateAudioMeters({ system: 0, mic: 0, preferred: 'mic', showSystem: false });
    updateExtractionWarning();
    updateEngineStatus();
  };

  const formatTimestamp = (seconds) => {
    const value = Number.isFinite(seconds) ? Math.max(0, Math.round(seconds)) : 0;
    const minutes = String(Math.floor(value / 60)).padStart(2, '0');
    const secs = String(value % 60).padStart(2, '0');
    return `[${minutes}:${secs}]`;
  };

  const appendTranscript = (transcript) => {
    if (!transcriptEl || !transcript) return;

    const ensureContainerReady = () => {
      if (transcriptEl.firstChild && transcriptEl.firstChild.textContent?.includes(copy.transcript.empty)) {
        transcriptEl.innerHTML = '';
      }
    };

    if (Array.isArray(transcript.segments) && transcript.segments.length) {
      ensureContainerReady();
      transcript.segments.forEach((segment) => {
        const text = (segment.text ?? '').trim();
        if (!text) return;
        const start = Number(segment.start ?? transcript.startSec ?? 0);
        const containerEl = document.createElement('div');
        containerEl.className = 'transcript-line';
        containerEl.innerHTML = html`<span class="muted">${formatTimestamp(start)}</span> ${text}`;
        transcriptEl.appendChild(containerEl);
      });
    } else if (transcript.text) {
      const text = transcript.text.trim();
      if (text) {
        ensureContainerReady();
        const start = Number(transcript.startSec ?? 0);
        const containerEl = document.createElement('div');
        containerEl.className = 'transcript-line';
        containerEl.innerHTML = html`<span class="muted">${formatTimestamp(start)}</span> ${text}`;
        transcriptEl.appendChild(containerEl);
      }
    }

    transcriptEl.scrollTop = transcriptEl.scrollHeight;
  };

  const renderRequirements = (items) => {
    state.requirements = Array.isArray(items) ? items : [];
    if (!requirementsEl) return;
    if (!state.requirements.length) {
      requirementsEl.innerHTML = `<p class="muted">${copy.requirements.empty}</p>`;
    } else {
      requirementsEl.innerHTML = renderRequirementGroups(state.requirements, copy);
    }
    if (state.appFactory.enabled) {
      state.appFactory.requirementsDirty = true;
      updateAppFactoryRequirementHint();
      maybeAutoSyncAppFactory();
    }
  };

  const renderDeferredDecisions = (items) => {
    state.deferredDecisions = Array.isArray(items) ? items : [];
    if (!deferredDecisionsEl) return;
    if (!state.deferredDecisions.length) {
      deferredDecisionsEl.innerHTML = `<p class="muted">${copy.deferredDecisions?.empty ?? 'Noch keine vertagten Entscheidungen vorhanden.'}</p>`;
      return;
    }
    deferredDecisionsEl.innerHTML = state.deferredDecisions
      .map((decision) => renderDeferredDecisionCard(decision, copy))
      .join('');
  };

  const stopSession = async ({ silent = false } = {}) => {
    if (!state.active) {
      updateAudioMeters({ system: 0, mic: 0, preferred: 'mic', showSystem: false });
      return;
    }
    setButtonPending();
    try {
      state.recorder?.stop();
      if (!silent) {
        await state.client?.finalize();
      }
    } catch (error) {
      console.error('Failed to stop live session', error);
      if (!silent) {
        updateStatus(copy.statuses.stopError);
      }
    } finally {
      await releaseWakeLock();
      state.recorder = null;
      state.client = null;
      state.active = false;
      startButton.textContent = copy.buttons.start;
      state.awaitingFirstChunk = false;
      resetButtonStyle();
      updateAudioMeters({ system: 0, mic: 0, preferred: 'mic', showSystem: false });
    }
  };

  const startSession = async () => {
    if (state.active) {
      await stopSession();
      return;
    }
    if (state.wizardOpen) {
      return;
    }

    shareFeedback.textContent = '';
    updateExtractionWarning();

    try {
      state.wizardOpen = true;
      const wizardResult = await openAudioSetupWizard();
      state.wizardOpen = false;

      if (!wizardResult) {
        updateStatus(copy.info.statusReady);
        updateAudioMeters({ system: 0, mic: 0, preferred: 'mic', showSystem: false });
        return;
      }

      let micStream = wizardResult.micStream ?? null;
      let systemStream = wizardResult.screenStream && wizardResult.screenStream.getAudioTracks().length
        ? wizardResult.screenStream
        : null;
      let usedSystemAudio = Boolean(systemStream);

      if (!micStream) {
        updateStatus(copy.statuses.startError);
        if (systemStream) {
          systemStream.getTracks().forEach((track) => {
            try {
              track.stop();
            } catch (error) {
              console.warn('Failed to stop system stream track', error);
            }
          });
        }
        return;
      }

      if (!usedSystemAudio) {
        updateStatus(copy.statuses.systemAudioUnsupported);
      }

      updateAudioMeters({
        system: 0,
        mic: 0,
        preferred: usedSystemAudio ? 'system' : 'mic',
        showSystem: usedSystemAudio,
      });

      updateStatus(copy.statuses.connecting);

      const client = new LiveSessionClient({
        onStatus: updateStatus,
        onTranscript: appendTranscript,
        onRequirements: renderRequirements,
        onDeferredDecisions: renderDeferredDecisions,
        onLanguage: (language) => {
          if (!language) return;
          const normalized = String(language).toLowerCase();
          state.language = normalized;
          if (normalized !== state.selectedLanguage) {
            state.languageManual = false;
          }
          updateLanguageStatus(state.language, { manual: state.languageManual });
        },
        onEngine: (engine) => {
          if (!engine) return;
          state.engine = engine;
          refreshEngineButtons();
        },
        onWarnings: (warnings) => {
          updateExtractionWarning(warnings);
        },
        onChunkStatus: (chunkStatus) => {
          if (!chunkStatus) return;
          if (chunkStatus.buffered) {
            if (state.awaitingFirstChunk) {
              setButtonPending();
            }
            updateStatus(copy.statuses.chunkBuffered);
            return;
          }
          if (chunkStatus.skipped) {
            if (state.awaitingFirstChunk) {
              setButtonPending();
            }
            updateStatus(copy.statuses.chunkSkipped);
            return;
          }
          if (chunkStatus && chunkStatus.skipped === false && state.awaitingFirstChunk) {
            state.awaitingFirstChunk = false;
            setButtonSuccess();
            updateStatus(copy.statuses.sessionStreaming);
          }
        },
      });

      state.language = state.selectedLanguage;
      state.languageManual = true;
      updateLanguageStatus(state.selectedLanguage, { manual: true });

      const startPayload = {};
      if (state.engine) {
        startPayload.engine = state.engine;
      }
      if (state.selectedLanguage) {
        startPayload.language = state.selectedLanguage;
      }
      const customerId = customerInput?.value?.trim();
      const projectId = projectInput?.value?.trim();
      const meetingDate = meetingInput?.value?.trim() || getTodayDateString();
      if (customerId) {
        startPayload.customerId = customerId;
      }
      if (projectId) {
        startPayload.projectId = projectId;
      }
      if (meetingDate) {
        startPayload.meetingDate = meetingDate;
      }
      if (state.domain) {
        startPayload.domain = state.domain;
      }

      let sessionMeta = null;
      const pendingRetryToken = window.sessionStorage.getItem('billingStartRetryToken');
      if (pendingRetryToken) {
        try {
          sessionMeta = await retryBillingStart({ retryToken: pendingRetryToken });
          if (sessionMeta?.retryConsumed) {
            window.sessionStorage.removeItem('billingStartRetryToken');
          }
        } catch (retryError) {
          if (retryError?.payload?.retryToken) {
            window.sessionStorage.setItem('billingStartRetryToken', retryError.payload.retryToken);
            throw retryError;
          }
          window.sessionStorage.removeItem('billingStartRetryToken');
          if (retryError?.status !== 400 && retryError?.status !== 409) {
            throw retryError;
          }
        }
      }
      if (!sessionMeta) {
        sessionMeta = await client.createSession(startPayload);
      }
      if (sessionMeta?.transcriptionEngine && sessionMeta.transcriptionEngine !== state.engine) {
        state.engine = sessionMeta.transcriptionEngine;
        refreshEngineButtons();
      }

      const maxChunkMs = Math.max(500, (sessionMeta?.chunkMaxSeconds ?? 4) * 1000);
      const desiredTimesliceMs = Math.max(500, sessionMeta?.chunkTimesliceMs ?? 2000);
      const timesliceMs = Math.min(desiredTimesliceMs, maxChunkMs);

      const handleLevels = ({ system = 0, mic = 0, preferred } = {}) => {
        const showSystem = usedSystemAudio;
        const effectivePreferred = showSystem
          ? (preferred ?? (system >= mic ? 'system' : 'mic'))
          : 'mic';
        updateAudioMeters({
          system: showSystem ? system : 0,
          mic,
          preferred: effectivePreferred,
          showSystem,
        });
      };

      const recorder = new LiveRecorder({
        timesliceMs,
        onChunk: (chunk) => client.sendChunk({ ...chunk, language: state.selectedLanguage }).catch((error) => {
          console.error('Chunk send failed', error);
          updateStatus(copy.statuses.chunkSendFailed);
          if (state.awaitingFirstChunk) {
            setButtonPending();
          }
        }),
        onLevels: handleLevels,
      });

      let started = false;
      try {
        await recorder.start({
          useMic: true,
          useSystemAudio: usedSystemAudio,
          mode: 'mix',
          streams: {
            micStream,
            systemStream,
          },
        });
        started = true;
      } catch (recorderError) {
        console.warn('System audio could not start, trying microphone only.', recorderError);
        if (usedSystemAudio) {
          try {
            const micTrack = micStream?.getAudioTracks?.()[0];
            if (!micTrack || micTrack.readyState === 'ended') {
              micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            }
            await recorder.start({
              useMic: true,
              useSystemAudio: false,
              mode: 'mix',
              streams: {
                micStream,
                systemStream: null,
              },
            });
            started = true;
            usedSystemAudio = false;
            updateStatus(copy.statuses.systemAudioUnsupported);
            updateAudioMeters({
              system: 0,
              mic: 0,
              preferred: 'mic',
              showSystem: false,
            });
          } catch (fallbackError) {
            throw fallbackError;
          }
        } else {
          throw recorderError;
        }
      }

      if (!started) {
        throw new Error(copy.statuses.startError);
      }

      state.recorder = recorder;
      state.client = client;
      state.active = true;
      startButton.textContent = copy.buttons.stop;
      state.awaitingFirstChunk = true;
      setButtonPending();
      transcriptEl.innerHTML = '';
      updateStatus(usedSystemAudio ? copy.statuses.waitingAudio : copy.statuses.waitingAudioMic);
      handleLevels({ system: 0, mic: 0 });
      await requestWakeLock();
    } catch (error) {
      console.error('Failed to start live session', error);
      if (error?.payload?.retryToken) {
        window.sessionStorage.setItem('billingStartRetryToken', error.payload.retryToken);
      }
      if (error?.payload?.code === 'PACKAGE_M_LIMIT_REACHED') {
        updateStatus(`${error?.message ?? copy.statuses.startError} Öffne Profil > Tarif oder Abrechnungen.`);
      } else if (error?.payload?.code === 'BILLING_REQUIRED' || error?.payload?.code === 'PLAN_CHANGE_PENDING' || error?.payload?.code === 'PAYMENT_FAILED') {
        updateStatus(`${error?.message ?? copy.statuses.startError} Bitte Profil > Abrechnungen prüfen.`);
      } else {
        updateStatus(error?.message ?? copy.statuses.startError);
      }
      await stopSession({ silent: true });
      updateAudioMeters({ system: 0, mic: 0, preferred: 'mic', showSystem: false });
    } finally {
      state.wizardOpen = false;
    }
  };


  startButton?.addEventListener('click', () => {
    startSession();
  });

  shareButton?.addEventListener('click', async () => {
    if (!state.requirements.length) {
      shareFeedback.textContent = copy.requirements.shareMissing;
      return;
    }
    const payload = {
      createdAt: new Date().toISOString(),
      requirements: state.requirements,
      sharedBy: (() => {
        const user = getCurrentUser();
        if (!user) return null;
        const nameParts = [user.profile?.firstName, user.profile?.lastName].filter((part) => part && part.trim());
        return {
          name: nameParts.length ? nameParts.join(' ') : undefined,
          email: user.email,
          company: user.profile?.company || undefined,
        };
      })(),
    };
    let encoded;
    try {
      encoded = encodeJsonPayload(payload);
    } catch (error) {
      console.error('Encoding share payload failed', error);
      shareFeedback.textContent = copy.requirements.shareCreateError;
      return;
    }
    const shareUrl = `${window.location.origin}${window.location.pathname}#/live/share/${encoded}`;
    if (shareLinkInput) {
      shareLinkInput.value = shareUrl;
      shareLinkInput.classList.remove('hidden');
      shareLinkInput.focus();
      shareLinkInput.select();
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      shareFeedback.textContent = copy.requirements.shareCopied;
      setTimeout(() => {
        if (shareFeedback.textContent === copy.requirements.shareCopied) {
          shareFeedback.textContent = '';
        }
      }, 2000);
    } catch (error) {
      console.warn('Clipboard failed', error);
      shareFeedback.innerHTML = formatCopy(copy.requirements.shareClipboardError, { url: shareUrl });
    }
  });

  container.addEventListener('live:reset', () => {
    stopSession({ silent: true });
    resetView();
  }, { once: true });
};

export const renderLiveSessionView = (container) => {
  try {
    renderLiveSessionViewInternal(container);
  } catch (error) {
    console.error('Live session view failed', error);
    throw error;
  }
};
